import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from './TeamEditPopup.module.css';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import TeamColourDot from 'components/admin/TeamColourDot';
import CloseIcon from 'assets/images/icon_close.svg';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import { createClubTeam, updateClubTeam } from 'api/club/teamApi';
import { getErrorMessage } from 'utils/api';
import useChangeDetection from 'hooks/useChangeDetection';
import { TEAM_COLOURS, teamWordsOf } from 'utils/teamLabel';

// The longest name the server accepts (TeamRules.MaxNameLength).
const MAX_NAME_LENGTH = 50;

// Add or edit one of a club's teams: its name, an optional colour from the
// palette, and its place in the list. The club reads its own word for a team
// (House, Faction) throughout; see utils/teamLabel.js.
const TeamEditPopup = ({
  isOpen,
  onClose,
  clubID,
  team,
  mode = 'edit', // 'edit', 'add'
  defaultSortOrder = 1, // offered when adding: one after the last team
}) => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const words = teamWordsOf(selectedClub);

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    colour: '',
    sortOrder: '',
  });
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  const currentData = { formData };
  const { showPrompt, confirmLeave, cancelLeave, hasChanges, setShowPrompt } =
    useChangeDetection(originalData, currentData, isOpen);

  // Fill the form each time the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    const initialFormData =
      mode === 'edit' && team
        ? {
            name: team.name || '',
            colour: team.colour || '',
            sortOrder: team.sortOrder ?? '',
          }
        : { name: '', colour: '', sortOrder: defaultSortOrder };
    setFormData(initialFormData);
    setOriginalData({ formData: initialFormData });
    setErrors({});
  }, [isOpen, mode, team, defaultSortOrder]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const name = formData.name.trim();
    if (!name) {
      newErrors.name = `The ${words.lower.team} name is required`;
    } else if (name.length > MAX_NAME_LENGTH) {
      newErrors.name = `The ${words.lower.team} name must be ${MAX_NAME_LENGTH} characters or fewer`;
    }

    const sortOrder = parseInt(formData.sortOrder, 10);
    if (formData.sortOrder === '' || formData.sortOrder === null) {
      newErrors.sortOrder = 'The sort order is required';
    } else if (Number.isNaN(sortOrder)) {
      newErrors.sortOrder = 'The sort order must be a valid number';
    } else if (sortOrder < 0) {
      newErrors.sortOrder = 'The sort order cannot be less than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (mode === 'edit' && !hasChanges()) {
      onClose(false);
      return;
    }

    // An empty colour is sent as '', which the server reads as "no colour".
    const editedTeam = {
      name: formData.name.trim(),
      colour: formData.colour || '',
      sortOrder: parseInt(formData.sortOrder, 10),
    };

    setIsLoading(true);
    try {
      if (mode === 'edit') {
        await updateClubTeam(clubID, team.teamID, editedTeam);
        toast.success(`${words.team} updated successfully`);
      } else {
        await createClubTeam(clubID, editedTeam);
        toast.success(`${words.team} added successfully`);
      }
      onClose(true);
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (hasChanges()) {
      setShowPrompt(true);
      return;
    }
    onClose(false);
  };

  const handleConfirmLeave = () => {
    confirmLeave();
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modalOverlay">
        <div className="modal bigModal">
          <div className="modalContent">
            <div className="modalTitle">
              {mode === 'add' ? `Add ${words.team}` : `Edit ${words.team}`}
            </div>
            <div className={styles.form}>
              <div className={styles.inputGroup}>
                <label className={styles.label}>{words.team} Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  disabled={isLoading}
                  maxLength={MAX_NAME_LENGTH}
                  placeholder={`Enter ${words.lower.team} name`}
                />
                {errors.name && (
                  <span className={styles.errorText}>{errors.name}</span>
                )}
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Colour</label>
                <div className={styles.swatches}>
                  <button
                    type="button"
                    className={`${styles.swatch} ${formData.colour === '' ? styles.swatchSelected : ''}`}
                    onClick={() => handleInputChange('colour', '')}
                    disabled={isLoading}
                    aria-pressed={formData.colour === ''}
                  >
                    <TeamColourDot />
                    No colour
                  </button>
                  {TEAM_COLOURS.map((colour) => (
                    <button
                      type="button"
                      key={colour.value}
                      className={`${styles.swatch} ${formData.colour === colour.value ? styles.swatchSelected : ''}`}
                      onClick={() => handleInputChange('colour', colour.value)}
                      disabled={isLoading}
                      aria-pressed={formData.colour === colour.value}
                    >
                      <TeamColourDot colour={colour.value} />
                      {colour.name}
                    </button>
                  ))}
                </div>
                <div className={styles.helpText}>
                  The colour is shown as a dot beside the {words.lower.team}
                  &apos;s name.
                </div>
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Sort Order</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) =>
                    handleInputChange('sortOrder', e.target.value)
                  }
                  className={`${styles.input} ${errors.sortOrder ? styles.inputError : ''}`}
                  disabled={isLoading}
                  placeholder="Enter Sort Order"
                />
                {errors.sortOrder && (
                  <span className={styles.errorText}>{errors.sortOrder}</span>
                )}
              </div>
              <div className={styles.buttons}>
                <CustomNewButton
                  onClick={handleSave}
                  label="Save"
                  large={true}
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="goBack" onClick={handleClose}>
              <div className="goBackImage">
                <img src={BackIcon} alt="back" />
              </div>
              <div className="goBackText">Back</div>
            </div>
            <div className="closeBtn" onClick={handleClose}>
              <img src={CloseIcon} alt="close" />
            </div>
          </div>
        </div>
      </div>

      <CustomNewConfirm
        isOpen={showPrompt}
        onClose={cancelLeave}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to leave without saving?"
        onConfirm={handleConfirmLeave}
        confirmButtonText="Discard Changes"
        confirmButtonVariant="danger"
        onCancel={cancelLeave}
        cancelButtonText="Continue Editing"
      />
    </>
  );
};

TeamEditPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clubID: PropTypes.string,
  mode: PropTypes.string,
  team: PropTypes.object,
  defaultSortOrder: PropTypes.number,
};

export default TeamEditPopup;
